import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import Bugsnag from '@bugsnag/js';
import { trackResumedBriefEvent } from '@99designs/apps/spa/pages/create/events';
import AppendStructuredBriefDesigners from '@99designs/apps/spa/pages/create/mutations/AppendStructuredBriefDesigners';
import CopyBrief from '@99designs/apps/spa/pages/create/mutations/CopyBrief';
import BriefEntryPage from '@99designs/apps/spa/pages/create/briefs/BriefEntryPage';
import React, { useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { linkTo } from '@99designs/routing';
import { parseQueryString } from '@99designs/apps/spa/pages/create/util/queryString';
import { BriefEntryComponent } from '@99designs/apps/spa/pages/create/briefs/BriefEntry.generated';
import { useCreateBriefWithSlugMutation } from '../mutations/CreateBriefWithSlug/CreateBriefWithSlug.generated';

function BriefEntry() {
    const [isRunningMutation, setIsRunningMutation] = useState(false);
    const [createBrief, { loading: loadingCreateBrief, data }] = useCreateBriefWithSlugMutation();
    const { search } = useLocation();
    const navigate = useNavigate();
    const { designerId, category } = parseQueryString(search);

    if (data) {
        return <Navigate replace to={linkTo.brief(data.brief.id, 1)} />;
    }

    return (
        <BriefEntryComponent variables={{ id: designerId }}>
            {({ loading, error, data }) => {
                if (loading) {
                    return <LoadableElement variations={['fade-in']} isLoading={true} />;
                }

                if (error) {
                    return (
                        <Navigate
                            replace
                            to={{ pathname: linkTo.briefCreate('project'), search }}
                        />
                    );
                }

                if (data) {
                    if (!data.me.user || (!data.drafts.length && !data.launched.length)) {
                        return (
                            <Navigate
                                replace
                                to={{ pathname: linkTo.briefCreate('project'), search }}
                            />
                        );
                    }

                    return (
                        <AppendStructuredBriefDesigners>
                            {(appendDesigners) => (
                                <CopyBrief>
                                    {(copyBrief) => {
                                        if (loadingCreateBrief || isRunningMutation) {
                                            return (
                                                <LoadableElement
                                                    variations={['fade-in']}
                                                    isLoading={true}
                                                />
                                            );
                                        }

                                        if (!data.me.user) {
                                            throw new Error('User is null');
                                        }

                                        return (
                                            <BriefEntryPage
                                                designer={data.designer}
                                                recentDrafts={data.drafts}
                                                recentLaunchedBriefs={data.launched}
                                                createBriefAction={() => {
                                                    createBrief({
                                                        variables: {
                                                            slug: 'project',
                                                            designerIds: data.designer
                                                                ? [parseInt(data.designer.id)]
                                                                : [],
                                                            fields: {
                                                                category,
                                                            },
                                                        },
                                                    });
                                                }}
                                                copyBriefAction={async (brief) => {
                                                    setIsRunningMutation(true);

                                                    const res = await copyBrief({
                                                        id: brief.id,
                                                        designerIds: data.designer
                                                            ? [parseInt(data.designer.id)]
                                                            : [],
                                                    });

                                                    if (res && res.data) {
                                                        navigate(
                                                            linkTo.brief(res.data.brief.id, 1)
                                                        );
                                                    }
                                                }}
                                                resumeBriefAction={(brief) => {
                                                    trackResumedBriefEvent({
                                                        id: brief.id,
                                                    });
                                                    if (designerId) {
                                                        setIsRunningMutation(true);

                                                        appendDesigners({
                                                            id: brief.id,
                                                            designerIds: [designerId],
                                                            source: 'DIRECT',
                                                        })
                                                            .then(() =>
                                                                navigate(linkTo.brief(brief.id, 1))
                                                            )
                                                            .catch(Bugsnag.notify);
                                                    } else {
                                                        navigate(linkTo.brief(brief.id, 1));
                                                    }
                                                }}
                                                user={data.me.user}
                                            />
                                        );
                                    }}
                                </CopyBrief>
                            )}
                        </AppendStructuredBriefDesigners>
                    );
                }
                return null;
            }}
        </BriefEntryComponent>
    );
}

export default BriefEntry;
